import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import { BaseSyntheticEvent, useState } from 'react';
import _ from 'lodash';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { setConnectedUser, setIsLogged, setToken } from '../../stores/authenticationStore';
import logoVidelio from '../../assets/images/videlio_logo_filed_color.svg';
import { setAlertData } from '../../stores/alertStore';
import { AlertSeverity } from '../../utils/enum/Alerts';

interface LocationState {
  from: {
    pathname: string;
  }
}

function Login() {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const initError = {
    emailError: false,
    emailHelper: '',
    passwordError: false,
    passwordHelper: '',
  };
  const navigate = useNavigate();

  const location = useLocation();
  const locationState = location.state ? location.state as LocationState : {} as LocationState;

  const from = locationState.from?.pathname || '/';

  const [isError, setIsError] = useState(initError);
  const [loginInfo, setLoginInfo] = useState({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const handleChange = (event: any) => {
    setLoginInfo({ ...loginInfo, [event.target.name]: event.target.value });
  };

  const validateEmail = (email: string) => email.match(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );

  const doLogin = () => {
    axios.post(`${window._env_.REACT_APP_API_URI}/user/login`, loginInfo)
      .then((response) => {
        const { data } = response;
        const storage = {
          user: data.data.user,
          token: data.token
        };
        localStorage.setItem('user', JSON.stringify(storage));
        dispatch(setIsLogged({ isLogged: true }));
        dispatch(setConnectedUser({ connectedUser: data.data.user }));
        dispatch(setToken({ token: data.token }));
        // navigate(from, { replace: true });
        navigate('/', { replace: true });
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  };

  const onLogin = (event: BaseSyntheticEvent) => {
    event.preventDefault();
    const tempError = { ...initError };
    if (loginInfo.email === '') {
      tempError.emailError = true;
      tempError.emailHelper = 'Login.emailRequired';
    }
    if (!validateEmail(loginInfo.email)) {
      tempError.emailError = true;
      tempError.emailHelper = 'Login.wrongFormat';
    }
    if (loginInfo.password === '') {
      tempError.passwordError = true;
      tempError.passwordHelper = 'Login.passwordRequired';
    }
    if (loginInfo.password.length < 6) {
      tempError.passwordError = true;
      tempError.passwordHelper = 'Login.passwordTooShort';
    }
    setIsError(tempError);
    if (_.isEqual(tempError, initError)) {
      doLogin();
    }
  };

  const redirectPasswordRecovery = () => {
    navigate('/password-recovery');
  };

  return (
    <div className="login">
      <img className="logo" src={logoVidelio} alt="logo videlio" />
      <div className="grid-item-1" />
      <div className="grid-item-2">
        <form onSubmit={onLogin} className="form">
          <h1>{t('Login.title')}</h1>
          <TextField
            variant="outlined"
            label={t('Login.email')}
            error={isError.emailError}
            helperText={isError.emailError ? t(isError.emailHelper) : ''}
            value={loginInfo.email}
            onChange={handleChange}
            type="email"
            name="email"
            placeholder="Ex. pat@example.com"
            required
          />
          <TextField
            variant="outlined"
            label={t('Login.password')}
            error={isError.passwordError}
            helperText={isError.passwordError ? t(isError.passwordHelper) : ''}
            value={loginInfo.password}
            onChange={handleChange}
            type={showPassword ? 'text' : 'password'}
            name="password"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Stack direction="row" spacing={2} alignSelf="center" width="100%">
            <Button fullWidth type="submit" value="submit" variant="contained">{t('Login.submit')}</Button>
            <Button fullWidth onClick={redirectPasswordRecovery} variant="outlined">{t('Login.forgotPassword')}</Button>
          </Stack>
        </form>
        <div className="bottom">
          <Button component={Link} to="/password-recovery">{t('Login.forgotPassword')}</Button>
        </div>
      </div>
    </div>
  );
}

export default Login;
